import React, { useState } from "react";
import { Row, Col, Button, Badge } from "react-bootstrap";
import {
  object,
  bool,
  array,
  func,
  number,
  string,
  oneOfType,
} from "prop-types";
import { fromJS } from "immutable";
import MultipleScoreScales from "components/DynamicScoreReportScales/MultipleScoreScales";
import { trackDynamicScoreReport } from "utils/trackingFunctions";
import { EVENT_EPP_JOB_FAMILY_SELECT } from "utils/trackingConstants";
import { checkIfUserHasDevelopAccess } from "utils/helperFunctions";
import criteriaScoreRangeIcon from "resources/images/dynamic_score_reports/personality/score-report/criteria-score-range-icon.png";
import CandidateReportMerchandisingBanner from "components/CandidateProfileComponent/CandidateReportMerchandisingBanner";
import { getAssessmentErrorAlert } from "../ScoreReportHelpers";
import TalentMobility from "../TalentMobility/TalentMobility";

const EPP = ({
  initialScoreRange,
  isInvalidTest,
  reportInfo,
  scoreRangeData,
  SubcategoryScoreColumns,
  testInfo,
  testScores,
  candidateInfo,
  isInsightUser,
  isVistaUser,
  fromCandidateProfile,
  onCopyCandidateToJob,
  moveEmailCandidatesPermission,
  companyAccountId,
  pipelineStageName,
  isSampleJob,
  selectedScoreRange,
  setSelectedScoreRange,
  userData,
  hsVersion,
  onRedirectToDevelop,
}) => {
  const [showRevertButton, setShowRevertButton] = useState(false);

  const handleClearScoreRangeSelection = () => {
    setShowRevertButton(false);
    if (initialScoreRange) {
      setSelectedScoreRange(initialScoreRange);
    } else {
      setSelectedScoreRange(null);
    }
  };

  const defaultScoreRange = testInfo.getIn(["baseline", "defaultBaseline"]);

  const interviewQuestions = selectedScoreRange
    ? reportInfo.getIn(["text", "questions", selectedScoreRange.rangeName])
    : null;

  const InterviewQuestionSections = [];

  if (interviewQuestions) {
    interviewQuestions.keySeq().forEach((trait) => {
      const interviewQuestionListItems = [];

      interviewQuestions
        .getIn([trait, "questions"])
        .forEach((question, index) => {
          interviewQuestionListItems.push(
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${trait}${index}`}>{question}</li>
          );
        });

      InterviewQuestionSections.push(
        <div key={trait}>
          <Badge className="badge-interview">{trait}</Badge>
          <p
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: interviewQuestions.getIn([trait, "preface"]),
            }}
          />
          <ul>{interviewQuestionListItems}</ul>
        </div>
      );
    });
  }

  const onClickJobFamily = (positionName) => {
    if (
      !selectedScoreRange ||
      (selectedScoreRange && selectedScoreRange.rangeName !== positionName)
    ) {
      trackDynamicScoreReport(EVENT_EPP_JOB_FAMILY_SELECT);

      const rangeString = positions.getIn([positionName, "ranges"]);
      const rangeArray = rangeString.split(",");
      const traitRangeObj = {};
      rangeArray.forEach((range) => {
        const traitArray = range.split("|");
        const [traitName, traitMin, traitMax] = traitArray;
        traitRangeObj[traitName] = { low: traitMin, high: traitMax };
      });
      setSelectedScoreRange({
        rangeName: positionName,
        defaultTraitRanges: fromJS(traitRangeObj),
      });
      setShowRevertButton(true);
    }
  };

  const jobFamilyRows = [];

  const jobMatches = reportInfo.getIn(["eppData", "jobMatches"]);
  const positions = reportInfo.getIn(["text", "positions"]);

  positions.keySeq().forEach((positionName) => {
    jobFamilyRows.push(
      <Row
        key={positionName}
        id={
          selectedScoreRange && positionName === selectedScoreRange.rangeName
            ? "position-name"
            : ""
        }
        className={`score-report-job-row py-2 my-1 ${
          selectedScoreRange &&
          positionName === selectedScoreRange.rangeName &&
          "active"
        }`}
        onClick={() => onClickJobFamily(positionName)}
        data-positionname={positionName}
      >
        <Col lg={9} className="mt-1 ml-1">
          <strong style={{ fontSize: "13px" }}>
            {positions.getIn([positionName, "title"]).slice(0, -6)}
          </strong>
        </Col>
        <Col className="d-flex justify-content-end">
          <strong style={{ fontSize: "16px" }}>
            {jobMatches.get(positionName.replace(/\W+/g, ""))}%
          </strong>
        </Col>
      </Row>
    );
  });

  const traits = reportInfo.getIn(["text", "traits"]);

  const firstColumnTraitScores = [];
  const secondColumnTraitScores = [];

  traits
    .sort((a, b) => a.get("name").localeCompare(b.get("name")))
    .keySeq()
    .forEach((traitAbbreviation) => {
      const traitName = traits
        .get(traitAbbreviation)
        .get("name")
        .replace(" ", "-")
        .toLowerCase();

      const scoreBox = (
        <Row key={traitAbbreviation} className="pr-0 mr-5">
          <Col className="px-0">
            <Row>
              <div className="score-explanation trait-header">
                <span style={{ fontSize: "16px" }}>
                  {traits.getIn([traitAbbreviation, "name"])}
                </span>
                <span style={{ fontSize: "20px" }}>
                  {testScores.get(`${traitAbbreviation}_adjPercentile`)}
                </span>
              </div>
              <img
                src={
                  // eslint-disable-next-line global-require
                  require(
                    `resources/images/dynamic_score_reports/personality/score-report/${traitName}-icon.png`
                  ).default
                }
                style={{ width: "36px" }}
                alt="Trait icon"
              />
            </Row>
            <p className="mt-2">
              {reportInfo.get("text").get(`${traitAbbreviation}_text`)}
            </p>
          </Col>
        </Row>
      );
      if (firstColumnTraitScores.length <= 5) {
        firstColumnTraitScores.push(scoreBox);
      } else {
        secondColumnTraitScores.push(scoreBox);
      }
    });

  const customScoreRanges = [];

  if (
    initialScoreRange &&
    !defaultScoreRange &&
    scoreRangeData &&
    scoreRangeData.get("scoreRanges")
  ) {
    scoreRangeData.get("scoreRanges").forEach((range) => {
      customScoreRanges.push(
        <div key={range}>
          {range.get("scoreRangeAbbrev")} (
          {parseInt(range.get("min_scoreRange"), 10)} -
          {parseInt(range.get("max_scoreRange"), 10)})
        </div>
      );
    });
  }

  const showTalentMobility =
    !isInsightUser &&
    !isVistaUser &&
    hsVersion >= 2 &&
    testInfo.get("talentMobility") &&
    testInfo.get("talentMobility").get("34") &&
    testInfo.get("talentMobility").get("34").size > 0;

  const isVideoInterviewEnabled = userData?.videoInterviewsEnabled === 1;
  const isDevelopEnabled = checkIfUserHasDevelopAccess(userData);
  const isFreeTrial = userData?.status === "free_trial";
  const isAutoRenewalRep =
    userData?.rep?.am?.userId === 304 || userData?.rep?.am?.userId === 305;

  return (
    <>
      <Row className="mx-1 mb-3 pt-4 pb-3">
        <Col lg={4} className="mr-4 px-0">
          {initialScoreRange && (
            <Row className="mb-4">
              <Col>
                <h2 className="score-detail-section-heading">
                  Selected Score Range
                </h2>
                <Row
                  className="selected-score-range-box epp"
                  onClick={handleClearScoreRangeSelection}
                >
                  <Col lg={2} className="pl-2">
                    <img
                      src={criteriaScoreRangeIcon}
                      alt="Criteria score range"
                    />
                  </Col>
                  <Col className="d-flex flex-column justify-content-center">
                    {defaultScoreRange ? (
                      <>
                        <strong>Criteria Score Range:</strong>
                        <p className="mb-0">
                          {positions
                            .getIn([initialScoreRange.rangeName, "title"])
                            .slice(0, -6)}{" "}
                          ({initialScoreRange.rangeMin} -{" "}
                          {initialScoreRange.rangeMax})
                        </p>
                      </>
                    ) : (
                      <>
                        <strong>Custom Score Range:</strong>
                        <strong>{initialScoreRange.rangeName}</strong>
                        {customScoreRanges}
                      </>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          <h2 className="score-detail-section-heading">
            Criteria Job Families
          </h2>
          {showRevertButton && (
            <Button
              className="p-0 font-weight-bold"
              variant="link"
              style={{ color: "#425cc7", fontSize: "13px" }}
              onClick={handleClearScoreRangeSelection}
            >
              {initialScoreRange
                ? "Display Selected Score Range"
                : "Clear Job Family Selection"}
            </Button>
          )}
          <div className="score-report-job-container mt-3 mb-4">
            {jobFamilyRows}
          </div>
          {selectedScoreRange &&
            positions.get(selectedScoreRange.rangeName) && (
              <div>
                <strong>Key Job Responsibilities:</strong>
                <p>
                  {positions.getIn([
                    selectedScoreRange.rangeName,
                    "description",
                  ])}
                </p>
                <strong>Example Job Titles:</strong>
                <p>
                  {positions.getIn([selectedScoreRange.rangeName, "jobTitles"])}
                </p>
              </div>
            )}
        </Col>
        <Col>
          <h2 className="score-detail-section-heading">Score Details</h2>
          <Row className="pt-3">
            <MultipleScoreScales
              testId={34}
              scores={testScores}
              maxScore={100}
              scoreRange={selectedScoreRange}
              fromEPPReport
              traits={traits}
            />
          </Row>
        </Col>
      </Row>
      {showTalentMobility ? (
        <TalentMobility
          talentMobilityData={testInfo.get("talentMobility").get("34")}
          candidateInfo={candidateInfo}
          fromCandidateProfile={fromCandidateProfile}
          onCopyCandidateToJob={onCopyCandidateToJob}
          moveEmailCandidatesPermission={moveEmailCandidatesPermission}
          companyAccountId={companyAccountId}
          pipelineStageName={pipelineStageName}
          isSampleJob={isSampleJob}
          userData={userData}
          onRedirectToDevelop={onRedirectToDevelop}
          isDevelopEnabled={isDevelopEnabled}
        />
      ) : null}
      <Row className="score-report-row">
        <Col>
          <h2 className="score-detail-section-heading">Score Explanation</h2>
          <Row className="ml-4 my-4">
            <Col>{firstColumnTraitScores}</Col>
            <Col>{secondColumnTraitScores}</Col>
          </Row>
        </Col>
      </Row>
      {selectedScoreRange && interviewQuestions && (
        <Row className="score-report-row">
          <Col>
            <h2 className="score-detail-section-heading">
              Interview Questions for{" "}
              {positions.getIn([selectedScoreRange.rangeName, "title"])}
            </h2>
            <p>
              You can use these questions during an interview to gain deeper
              insight into this candidate&apos;s assessment results. Because the
              power of structured interviewing lies in asking all candidates the
              same questions and evaluating them against the same criteria, we
              recommend using these questions as supplemental information only
            </p>
            <CandidateReportMerchandisingBanner
              reportName="EPP"
              isVideoInterviewEnabled={isVideoInterviewEnabled}
              isFreeTrial={isFreeTrial}
              isAutoRenewalRep={isAutoRenewalRep}
              userAccountId={userData?.userAccountId}
              isDevelopEnabled={isDevelopEnabled}
              onRedirectToDevelop={onRedirectToDevelop}
            />
            {InterviewQuestionSections}
          </Col>
        </Row>
      )}
      <Row className="score-report-row">
        <Col>
          {isInvalidTest &&
            getAssessmentErrorAlert(
              34,
              reportInfo.getIn(["details", "INC_score"])
            )}
          <h2 className="score-detail-section-heading">
            Validity and Response Style
          </h2>
          <p>
            {`The Validity and Response Style scales represent the individual's
            level of attention to the meaning of EPP statements (Inconsistent
            Responding) and tendency toward positive (Self-Enhancing) or negative
            (Self-Critical) self-presentation.`}
          </p>
          <Row style={{ margin: "0 2px 0 2px" }}>{SubcategoryScoreColumns}</Row>
        </Col>
      </Row>
    </>
  );
};

EPP.propTypes = {
  initialScoreRange: object,
  isInvalidTest: bool,
  reportInfo: object,
  scoreRangeData: object,
  SubcategoryScoreColumns: array,
  testInfo: object,
  testScores: object,
  candidateInfo: object,
  isInsightUser: bool,
  isVistaUser: bool,
  fromCandidateProfile: bool,
  onCopyCandidateToJob: func,
  moveEmailCandidatesPermission: bool,
  companyAccountId: number,
  pipelineStageName: oneOfType([string, bool]),
  isSampleJob: bool,
  selectedScoreRange: object,
  setSelectedScoreRange: func,
  userData: object,
  hsVersion: number,
  onRedirectToDevelop: func,
};

export default EPP;
